import { webStorage } from '../common/web-storage'
/**
 * @file locale persistence and initialization helpers
 */
const LOCALE_KEY = 'locale'

/**
 * Get the local language string to load the language json
 * @private
 * @param {Array<string>} locales
 * @returns string
 */
function getNavigatorLocale(locales) {
    const { language, languages, userLanguage } = navigator

    let navigatorLang
    if (languages && languages.length > 0) {
        navigatorLang = languages[0]
    } else {
        navigatorLang = language || userLanguage
    }

    return localeSanitizer(navigatorLang, locales)
}

/**
 * Returns the initial locale to be used by the the application
 * @param {Array<string>} locales supported locale strings
 * @returns {string} locale id
 */
export function getInitialLocale(locales) {
    return webStorage.read(LOCALE_KEY) || getNavigatorLocale(locales)
}

/**
 * Stores the provided locale, so that it is available for getInitialLocale() in the future
 * @param {string} locale the locale string to be persisted
 */
export function persistLocale(locale) {
    webStorage.save(LOCALE_KEY, locale)
}

/**
 * Converts the given locale string to a supported locale string by given config with fallback to en-gb
 * @param {string} locale - The desired locale string e.g. de-DE, en_US
 * @param {Array<string>} locales - The predefined config of locales which are supported
 * @returns {string} Supported locale strings e.g de-DE
 *
 */
export function localeSanitizer(locale, locales = ['en-GB']) {
    const localePattern = /^([a-z]{2})(?:[_-]([A-Z]{2}))?/

    // Define the hard fallback
    let correctLocale = 'en-GB'
    const match = locale.match(localePattern)

    if (match && match.length > 0) {
        const languageCode = match[1] && match[1].toLowerCase()
        const territoryCode = match[2] && match[2].toLowerCase()

        const localeConfig = {}

        // Build locale config from locales
        locales.forEach(element => {
            const referenceMatch = element.match(localePattern)
            const referenceLanguageCode = referenceMatch[1] && referenceMatch[1].toLowerCase()
            const referenceTerritoryCode = referenceMatch[2] && referenceMatch[2].toLowerCase()

            localeConfig[referenceLanguageCode] = localeConfig[referenceLanguageCode] || []
            localeConfig[referenceLanguageCode].push(referenceTerritoryCode)
        })

        // Get supported languages from config
        const languages = Object.keys(localeConfig)
        // Look if given language code is a supported language from config
        const matchLanguage = languages.find(language => language === languageCode)
        // Get supported territories from matched language
        const territories = localeConfig[matchLanguage]
        if (territories && territories.length > 0) {
            let selectedTerritoryCode = territories[0]
            // Defined fallback territory from language
            if (matchLanguage && territoryCode) {
                // Look if given territory is an supported territory from config by language
                if (territories.indexOf(territoryCode) > 0) {
                    selectedTerritoryCode = territoryCode
                }
            }
            // Build supported locale by matched language and territory
            correctLocale = `${matchLanguage}-${selectedTerritoryCode.toUpperCase()}`
        }
    }

    return correctLocale
}
